import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import React, { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export function StandardReportPage() {
  const { t } = useTranslation();
  const { links } = useSiteSettings();
  const trackRef = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [heroPos, setHeroPos] = useState('8%');

  useEffect(() => {
    const timer = setTimeout(() => {
      setHeroPos('62%');
    }, 450);
    return () => clearTimeout(timer);
  }, []);

  const getBand = (v: number) => {
    if (v < 33) return 'low';
    if (v > 66) return 'high';
    return 'mid';
  };

  const band = getBand(value);

  const bandsContent = {
    low: {
      label: 'Low reading',
      name: 'low',
      html: '<p>You lean towards the left-hand end of this scale. That is a genuine preference with genuine strengths &mdash; and one fairly predictable blind spot. The report names <strong>both</strong>, then asks what the blind spot costs you.</p>'
    },
    mid: {
      label: 'Mid-range reading',
      name: 'mid-range',
      html: "<p>You sit between the two ends. That isn't a safe middle &mdash; it usually means you can go either way, and the useful question is <strong>what decides which way you go</strong> on a given day.</p>"
    },
    high: {
      label: 'High reading',
      name: 'high',
      html: '<p>You lean towards the right-hand end. High does not mean better. Any strength taken far enough starts costing something, and the report is direct about <strong>where yours starts costing</strong>.</p>'
    }
  };

  const currentContent = bandsContent[band as keyof typeof bandsContent];

  const updateFromEvent = (clientX: number) => {
    if (!trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    let newValue = (x / rect.width) * 100;
    newValue = Math.max(0, Math.min(100, newValue));
    setValue(newValue);
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    setIsDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
    updateFromEvent(e.clientX);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (isDragging) {
      updateFromEvent(e.clientX);
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    setIsDragging(false);
    try {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    } catch {
      // ignore
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 10 : 4;
    let newValue = value;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { newValue -= step; e.preventDefault(); }
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { newValue += step; e.preventDefault(); }
    if (e.key === 'Home') { newValue = 0; e.preventDefault(); }
    if (e.key === 'End') { newValue = 100; e.preventDefault(); }
    newValue = Math.max(0, Math.min(100, Math.round(newValue)));
    setValue(newValue);
  };

  return (
    <>
      <Helmet>
        <title>Standard Personal Feedback Report | FIVE LENS Enneagram</title>
        <meta name="description" content="In-depth individual behavioural profile covering all five lenses. Used by certified practitioners for one-on-one coaching debriefs." />
        <meta property="og:title" content="Standard Personal Feedback Report | FIVE LENS Enneagram" />
        <meta property="og:description" content="In-depth individual behavioural profile covering all five lenses. Used by certified practitioners for one-on-one coaching debriefs." />
        <meta name="twitter:title" content="Standard Personal Feedback Report | FIVE LENS Enneagram" />
        <meta name="twitter:description" content="In-depth individual behavioural profile covering all five lenses. Used by certified practitioners for one-on-one coaching debriefs." />
      </Helmet>
      
      <div className="pfr pt-20">
        <header className="pfr__hero">
          <div className="pfr__wrap">
            <nav className="pfr-tier" aria-label="Report versions">
              <span className="pfr-tier__label">Report version</span>
              <span className="pfr-tier__item pfr-tier__item--on" aria-current="page">Standard</span>
              <Link className="pfr-tier__item" to="/reports/premium">Premium &rarr;</Link>
            </nav>
            <p className="pfr__eyebrow">FIVE LENS &middot; Personal Feedback Report Standard &middot; Preview</p>
            <h1>Everything in the report comes down to one mark on one line.</h1>
            <p className="pfr__lede">The Personal Feedback Report looks at you through five different models. Each reading is a marker on a scale &mdash; not a score out of ten, not a type you get filed under. This page shows you what those pages look like and how to read them.</p>

            <figure className="pfr-scale pfr-scale--hero" style={{ marginTop: 'clamp(34px, 5vw, 56px)' }} aria-hidden="true">
              <div className="pfr-scale__anchors"><span>One end of the scale</span><span>The other end</span></div>
              <div className="pfr-scale__band"></div>
              <div className="pfr-scale__rule">
                <div className="pfr-scale__marker" style={{ '--pos': heroPos } as React.CSSProperties}><span>M</span></div>
              </div>
            </figure>
            <p className="pfr__hero-caption">That small marker is the whole idea. It says where your answers placed you, relative to everyone else who has answered the same questions.</p>

            <ul className="pfr__hero-meta">
              <li>Standard report</li>
              <li>5 lenses</li>
              <li>24 pages</li>
              <li>Questionnaire: about 20 minutes</li>
              <li>Sample profile: Alison Winters</li>
            </ul>
          </div>
        </header>

        <section className="pfr__section">
          <div className="pfr__wrap">
            <div className="pfr__head">
              <div className="pfr__head-row">
                <p className="pfr__eyebrow">01 &mdash; The one skill you need</p>
                <div className="pfr__ticks"></div>
              </div>
              <h2>How to read a scale</h2>
              <p className="pfr__lede">Move the marker. Nothing about the scale changes &mdash; only where you sit on it, and what that tends to look like in ordinary life.</p>
            </div>

            <div className="pfr-demo">
              <div className="pfr-demo__panel">
                <p className="pfr-demo__factor">Curiosity</p>
                <p className="pfr-demo__sub">Example scale &middot; drag or use arrow keys</p>

                <div className="pfr-scale">
                  <div className="pfr-scale__anchors"><span>Sticks with what it knows</span><span>Goes looking</span></div>
                  <div 
                    className={`pfr-track ${isDragging ? 'is-dragging' : ''}`}
                    ref={trackRef}
                    role="slider" 
                    tabIndex={0}
                    aria-label="Example reading on the curiosity scale"
                    aria-valuemin={0} 
                    aria-valuemax={100} 
                    aria-valuenow={Math.round(value)} 
                    aria-valuetext={currentContent.label}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerUp}
                    onKeyDown={handleKeyDown}
                  >
                    <div className="pfr-scale__band" style={{ '--hue': 'var(--l5)' } as React.CSSProperties}></div>
                    <div className="pfr-scale__rule">
                      <div className="pfr-scale__marker" style={{ '--pos': `${value}%` } as React.CSSProperties}><span>M</span></div>
                    </div>
                  </div>
                </div>
                <p className="pfr-demo__hint">Position: {currentContent.name}</p>
              </div>

              <div>
                <span className="pfr-readout__label">{currentContent.label}</span>
                <div 
                  className="pfr-readout__body" 
                  dangerouslySetInnerHTML={{ __html: currentContent.html }}
                />

                <ul className="pfr-rules">
                  <li><b>i</b><span>There is <strong>no pass mark</strong>. Both ends of every scale have gifts and costs, and the report names both.</span></li>
                  <li><b>ii</b><span>It's <strong>relative</strong>. High means high compared with other people who answered, not high in some absolute sense.</span></li>
                  <li><b>iii</b><span>It's a <strong>snapshot</strong>. It describes where you are now, under your current circumstances &mdash; not a fixed trait you're stuck with.</span></li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="pfr__section">
          <div className="pfr__wrap">
            <div className="pfr__head">
              <div className="pfr__head-row">
                <p className="pfr__eyebrow">02 &mdash; What gets measured</p>
                <div className="pfr__ticks"></div>
              </div>
              <h2>Five lenses, five different questions</h2>
              <p className="pfr__lede">Each lens is a separate model with its own scales. Read together, they explain why two people with the same job description behave completely differently under pressure.</p>
            </div>

            <ol className="pfr-lenses">
              <li className="pfr-lens pfr-lens--1">
                <div className="pfr-lens__num">Lens 01</div>
                <div>
                  <h3>Personal Mastery</h3>
                  <p className="pfr-lens__q"><em>How settled are you in yourself?</em></p>
                </div>
                <div className="pfr-lens__what">
                  <p>Six readings that together suggest how well you cope when life gets difficult: how you view yourself, how much of a sense of direction you have, how you handle what you can't change, how much you want to learn, the mark you leave on other people, and whether you feel part of something bigger than your own circle.</p>
                  <ul className="pfr-lens__chips">
                    <li>6 scales</li><li>+ an overall index</li>
                  </ul>
                </div>
              </li>

              <li className="pfr-lens pfr-lens--2">
                <div className="pfr-lens__num">Lens 02</div>
                <div>
                  <h3>Emotional Resilience</h3>
                  <p className="pfr-lens__q"><em>How well do you recover?</em></p>
                </div>
                <div className="pfr-lens__what">
                  <p>Three readings, one for each of head, heart and gut: how flexibly you think when pressure mounts, how steady your emotions stay, and how much you believe you can handle what's in front of you. Resilience needs all three &mdash; a weak one drags the others down.</p>
                  <ul className="pfr-lens__chips">
                    <li>Flexibility</li><li>Emotional steadiness</li><li>Self-belief</li>
                  </ul>
                </div>
              </li>

              <li className="pfr-lens pfr-lens--3">
                <div className="pfr-lens__num">Lens 03</div>
                <div>
                  <h3>Social Drives</h3>
                  <p className="pfr-lens__q"><em>What are you quietly trying to secure?</em></p>
                </div>
                <div className="pfr-lens__what">
                  <p>Four drives, each a different need pulling at your behaviour: being safe, belonging somewhere, being recognised for what you do, and contributing to something beyond yourself. The report shows which one is loudest right now &mdash; and a loud lower drive usually explains behaviour that otherwise looks out of character.</p>
                  <ul className="pfr-lens__chips">
                    <li>Survive</li><li>Affiliate</li><li>Achieve</li><li>Transcend</li>
                  </ul>
                </div>
              </li>

              <li className="pfr-lens pfr-lens--4">
                <div className="pfr-lens__num">Lens 04</div>
                <div>
                  <h3>{t('framework.lens5.title', 'Energy Centres')}</h3>
                  <p className="pfr-lens__q"><em>Where do you go first &mdash; head, heart or gut?</em></p>
                </div>
                <div className="pfr-lens__what">
                  <p>Everyone has all three. Most people lean hard on one. This lens shows which centre you reach for by default: acting on instinct, feeling your way in, or thinking it through. It's the quickest explanation of why some colleagues want the analysis and others want to start.</p>
                  <ul className="pfr-lens__chips">
                    <li>Instinctual</li><li>Emotional</li><li>Intellectual</li>
                  </ul>
                </div>
              </li>

              <li className="pfr-lens pfr-lens--5">
                <div className="pfr-lens__num">Lens 05</div>
                <div>
                  <h3>The Enneagram</h3>
                  <p className="pfr-lens__q"><em>Which pattern do you fall back on under pressure?</em></p>
                </div>
                <div className="pfr-lens__what">
                  <p>Nine recognisable patterns of behaviour. You'll show a bit of all nine; the report highlights the one you lean on most, the one backing it up, and the one you use least &mdash; often the most useful of the three, because it points at what you habitually leave out.</p>
                  <ul className="pfr-lens__chips">
                    <li>1 Quality</li><li>2 Service</li><li>3 Delivery</li><li>4 Creativity</li><li>5 Insight</li><li>6 Vigilance</li><li>7 Vision</li><li>8 Drive</li><li>9 Harmony</li>
                  </ul>
                </div>
              </li>
            </ol>
          </div>
        </section>

        <section className="pfr__section">
          <div className="pfr__wrap">
            <div className="pfr__head">
              <div className="pfr__head-row">
                <p className="pfr__eyebrow">03 &mdash; What a page looks like</p>
                <div className="pfr__ticks"></div>
              </div>
              <h2>Every page is built the same way</h2>
              <p className="pfr__lede">Once you can read one page, you can read all twenty-four. Here's the layout, with the written feedback held back &mdash; that part belongs in your own report.</p>
            </div>

            <div className="pfr-page-wrap">
              <article className="pfr-page">
                <div className="pfr-page__bar">
                  <div className="pfr-page__brand">FIVE LENS<span className="pfr-page__tier">Standard</span></div>
                  <div className="pfr-page__title">
                    <strong>Factor D: Curiosity</strong>
                    <span>Are you willing to learn new things?</span>
                  </div>
                </div>

                <div className="pfr-page__cols">
                  <div className="pfr-page__col">
                    <p className="pfr-page__label">Your reading</p>
                    <div className="pfr-scale">
                      <div className="pfr-scale__band" style={{ '--hue': 'var(--l5)', height: '26px' } as React.CSSProperties}></div>
                      <div className="pfr-scale__rule">
                        <div className="pfr-scale__marker" style={{ '--pos': '45%' } as React.CSSProperties}><span>M</span></div>
                      </div>
                    </div>
                    <div className="pfr-page__anchors">
                      <div>
                        <b>Closed</b>
                        <ul><li>Rigid attitude</li><li>Avoidant</li><li>Unquestioning</li></ul>
                      </div>
                      <div>
                        <b>Open</b>
                        <ul><li>Flexible attitude</li><li>Engaging</li><li>Questioning</li></ul>
                      </div>
                    </div>
                    <ul className="pfr-redact" aria-label="Written feedback, withheld in this preview">
                      <li><span></span><span style={{ width: '88%' }}></span></li>
                      <li><span></span><span style={{ width: '64%' }}></span></li>
                      <li><span></span><span style={{ width: '79%' }}></span></li>
                    </ul>
                    <p className="pfr-page__stamp">Four to five plain-language statements about what this reading tends to look like in your day-to-day.</p>
                  </div>

                  <div className="pfr-page__col">
                    <p className="pfr-page__label">What this could mean to you</p>
                    <ul className="pfr-redact" aria-label="Written feedback, withheld in this preview">
                      <li><span></span><span></span><span style={{ width: '52%' }}></span></li>
                      <li><span></span><span style={{ width: '71%' }}></span></li>
                      <li><span></span><span></span><span style={{ width: '38%' }}></span></li>
                      <li><span></span><span style={{ width: '83%' }}></span></li>
                    </ul>
                    <p className="pfr-page__stamp">The consequences &mdash; where this shows up at work, in decisions, and in how other people experience you.</p>
                  </div>

                  <div className="pfr-page__col pfr-page__col--aside">
                    <p className="pfr-page__label">Insights</p>
                    <ul className="pfr-redact" aria-label="Written feedback, withheld in this preview">
                      <li><span></span><span></span><span style={{ width: '60%' }}></span></li>
                    </ul>
                    <p className="pfr-page__stamp" style={{ marginBottom: '4px' }}>A short piece of context from the research behind this factor.</p>

                    <p className="pfr-page__label" style={{ marginTop: '26px' }}>Reflections</p>
                    <ul className="pfr-redact" aria-label="Coaching questions, withheld in this preview">
                      <li><span style={{ width: '92%' }}></span></li>
                      <li><span style={{ width: '76%' }}></span></li>
                      <li><span style={{ width: '84%' }}></span></li>
                    </ul>
                    <p className="pfr-page__stamp">Three or four questions to answer honestly, on your own or with a coach. This is where the work actually happens.</p>
                  </div>
                </div>
              </article>
            </div>

            <div className="pfr-callouts">
              <div className="pfr-callout">
                <b>01</b>
                <p><strong>Left column: where you sit.</strong> The scale, the words that describe each end, and what your particular reading suggests.</p>
              </div>
              <div className="pfr-callout">
                <b>02</b>
                <p><strong>Middle column: so what.</strong> The practical consequences &mdash; the part people quote back to themselves months later.</p>
              </div>
              <div className="pfr-callout">
                <b>03</b>
                <p><strong>Right column: what to do with it.</strong> Context, then questions. The report never tells you what to change; it asks you better questions about it.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="pfr__section">
          <div className="pfr__wrap">
            <div className="pfr__head">
              <div className="pfr__head-row">
                <p className="pfr__eyebrow">04 &mdash; Setting expectations</p>
                <div className="pfr__ticks"></div>
              </div>
              <h2>Three things it isn't</h2>
            </div>

            <ul className="pfr-isnt">
              <li>
                <b>Not a test</b>
                <p>There's nothing to pass. No reading is better than another one, and there's no combination that makes you a stronger candidate for anything.</p>
              </li>
              <li>
                <b>Not a diagnosis</b>
                <p>It's a development tool, not a clinical assessment. It describes patterns of behaviour; it doesn't say anything about your mental health.</p>
              </li>
              <li>
                <b>Not a permanent box</b>
                <p>Readings move as circumstances and self-awareness change. People who redo the questionnaire years later often see exactly where their life shifted.</p>
              </li>
            </ul>
          </div>
        </section>

        <section className="pfr__section">
          <div className="pfr__wrap">
            <div className="pfr__head">
              <div className="pfr__head-row">
                <p className="pfr__eyebrow">05 &mdash; How it runs</p>
                <div className="pfr__ticks"></div>
              </div>
              <h2>From questionnaire to something you actually use</h2>
            </div>

            <ol className="pfr-steps">
              <li>
                <div>
                  <b>Answer the questionnaire</b>
                  <p>Online, roughly twenty minutes. Answer as you actually are rather than as you'd like to be &mdash; the report is only as useful as the honesty you put into it.</p>
                </div>
              </li>
              <li>
                <div>
                  <b>Receive your report</b>
                  <p>A private PDF, addressed to you. It isn't sent to your manager or your HR team unless you've agreed to that upfront.</p>
                </div>
              </li>
              <li>
                <div>
                  <b>Read it once, straight through</b>
                  <p>Don't stop to argue with it. Note what stings &mdash; the lines that irritate you are usually the ones worth returning to.</p>
                </div>
              </li>
              <li>
                <div>
                  <b>Debrief with a practitioner</b>
                  <p>Usually 60 to 90 minutes. A certified practitioner takes you through the readings, checks them against your own experience, and helps you decide which one or two are worth acting on. <Link to="/for-individuals#request-intro" className="text-rule-3 underline font-medium hover:text-ink transition-colors">Request a debrief</Link> or <Link to="/find-a-practitioner" className="text-rule-3 underline font-medium hover:text-ink transition-colors">find an accredited coach</Link>.</p>
                </div>
              </li>
              <li>
                <div>
                  <b>Work the reflection questions</b>
                  <p>Each page ends with questions rather than instructions. Answered properly, over weeks, they're where the change comes from &mdash; often alongside coaching or a team programme.</p>
                </div>
              </li>
            </ol>
          </div>
        </section>

        <section className="pfr__section" style={{ borderTop: 0 }}>
          <div className="pfr__wrap">
            <div className="pfr-cta">
              <div>
                <h2>Want to see your own readings?</h2>
                <p>Talk to a certified practitioner about running the FIVE LENS Questionnaire for yourself, your leadership team, or your organisation.</p>
              </div>
              <div className="pfr-cta__actions">
                <a className="pfr-btn pfr-btn--solid" href={links.bookingUrl} target="_blank" rel="noopener noreferrer">Book a conversation</a>
                <Link className="pfr-btn pfr-btn--ghost" to="/reports/premium">See the Premium report</Link>
              </div>
            </div>
          </div>
        </section>

        <footer className="pfr__foot">
          <div className="pfr__wrap">
            <p>This page is an illustrative preview. Scale positions, sample names and page content shown here are for demonstration only and are not drawn from any individual's report. The FIVE LENS Questionnaire, the Personal Feedback Report and its interpretive content are the property of FIVE LENS Limited. &copy; FIVE LENS Limited.</p>
          </div>
        </footer>
      </div>
    </>
  );
}
